import * as React from "react";
import { Control, useFieldArray, useWatch } from "react-hook-form";
import { Text, ActionIcon, Grid, Stack, Group, Divider } from "@mantine/core";
import {
  IconChevronRight,
  IconChevronDown,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";

import {
  CMultiSelect,
  CSelect,
  CTextarea,
  CTextInput,
} from "@supportinitiative/react-hook-form-mantine";

/* import Where from "./Where";
import Having from "./Having";
import Order from "./Order";
import Columns from "./Columns"; */
import { useDataset } from "../../../../hooks";
import { columnTypes, getColumnsLOV } from "../../../UseCaseUtils";
import { UseCaseCVMContext } from "../../../UseCaseCVMContext";
import { UseCaseType } from "@supportinitiative/common";

function Query({
  name,
  control,
  datasetId,
  recon,
  label,
}: {
  name:
    | `definition.recons.${number}.definition.actions.${number}.definition.left`
    | `definition.recons.${number}.definition.actions.${number}.definition.right`
    | `definition.recons.${number}.definition.actions.${number}.definition.searchFor.left`
    | `definition.recons.${number}.definition.actions.${number}.definition.searchIn.left`
    | "left"
    | "right"
    | "definition.left"
    | "definition.right";
  control: Control<any>;
  datasetId: string;
  recon: UseCaseType["definition"]["recons"][number];
  label?: string;
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);

  let [isOpen, setIsOpen] = React.useState(false);

  const dataset = recon.definition.datasets.find((d) => d.id === datasetId);

  const { data: datasetDefinition } = useDataset(
    useCaseFormState.entityId,
    dataset?.datasetId
  );

  const additionalColumnsArray = useFieldArray({
    control,
    name: `${name}.additionalColumns`,
  });

  return (
    <Stack>
      <div
        style={{
          display: "flex",
          cursor: "pointer",
          padding: "5px 0px",
        }}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        {isOpen ? <IconChevronDown /> : <IconChevronRight />}

        <Text fw={500} style={{ fontSize: "14px" }}>
          {label ? label : "Query"}
        </Text>
      </div>

      {isOpen && (
        <Stack gap="0">
          <Grid align="flex-start" gutter={"xs"}>
            <Grid.Col span={12}>
              <CMultiSelect
                name={`${name}.columns`}
                control={control}
                searchable
                required
                data={getColumnsLOV(
                  datasetDefinition,
                  dataset?.columns || [],
                  dataset?.additionalColumns || []
                )}
                label={"Columns"}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Group align="center" mt="lg">
            <Text fw={500} style={{ fontSize: "14px" }}>
              Additional Columns
            </Text>
            <ActionIcon
              size="sm"
              variant="white"
              color="gray"
              onClick={() => {
                additionalColumnsArray.append({
                  name: "",
                  type: "TEXT",
                  label: "",
                  genFunction: "",
                  notNull: "NO",
                });
              }}
              disabled={useCaseFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>

          {additionalColumnsArray.fields.length === 0 ? (
            <Grid align="stretch" gutter={"xs"} mb="xs">
              <Grid.Col span={12}>
                <Divider
                  label="No Additional Columns"
                  mt="xs"
                  labelPosition="left"
                />
              </Grid.Col>
            </Grid>
          ) : (
            <Stack gap="0" mb="xs">
              <Grid align="stretch" gutter={"xs"} mb="xs">
                <Grid.Col span={12}>
                  {additionalColumnsArray.fields.map((field, index) => {
                    return (
                      <Grid
                        align="stretch"
                        gutter={"xs"}
                        mb="xs"
                        key={field.id}
                      >
                        <Grid.Col span={2}>
                          <CTextInput
                            name={`${name}.additionalColumns.${index}.name`}
                            control={control}
                            showErrorText={false}
                            label={index === 0 ? "Name" : ""}
                            required
                            disabled={useCaseFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <CSelect
                            name={`${name}.additionalColumns.${index}.type`}
                            control={control}
                            label={index === 0 ? "Type" : ""}
                            data={columnTypes}
                            showErrorText={false}
                            required
                            disabled={useCaseFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>

                        <Grid.Col span={6}>
                          <CTextInput
                            name={`${name}.additionalColumns.${index}.genFunction`}
                            control={control}
                            showErrorText={false}
                            label={index === 0 ? "Gen Function" : ""}
                            disabled={useCaseFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>

                        <Grid.Col
                          span={1}
                          style={{ display: "flex", alignItems: "flex-end" }}
                        >
                          <ActionIcon
                            size="sm"
                            variant="white"
                            color="gray"
                            onClick={() => {
                              additionalColumnsArray.remove(index);
                            }}
                            disabled={useCaseFormState.actionOptions.disableAll}
                          >
                            <IconTrash />
                          </ActionIcon>
                        </Grid.Col>
                      </Grid>
                    );
                  })}
                </Grid.Col>
              </Grid>
            </Stack>
          )}

          <Grid align="flex-start" gutter={"xs"}>
            <Grid.Col span={12}>
              <CTextarea
                name={`${name}.where`}
                control={control}
                autosize
                minRows={2}
                maxRows={20}
                label="Where"
                aria-label="Where"
                showErrorText={false}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="flex-start" gutter={"xs"}>
            <Grid.Col span={12}>
              <CTextarea
                name={`${name}.having`}
                control={control}
                autosize
                minRows={2}
                maxRows={20}
                label="Having"
                aria-label="Having"
                showErrorText={false}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Grid align="flex-start" gutter={"xs"}>
            <Grid.Col span={12}>
              <CTextarea
                name={`${name}.order`}
                control={control}
                autosize
                minRows={2}
                maxRows={20}
                label="Order"
                aria-label="Order"
                showErrorText={false}
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          {/* <Where name={`${name}.where`} control={control} />
          <Having name={`${name}.having`} control={control} />
          <Order name={`${name}.order`} control={control} /> */}
        </Stack>
      )}
    </Stack>
  );
}

export default Query;
